import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * ssstufff: clicking a size on a product card adds that variant to the cart.
 *
 * The buttons come from snippets/product-card-sizes.liquid. One delegated listener on the
 * document covers cards that arrive later (infinite scroll, filter morphs, recommendations).
 * The request and the CartAddEvent mirror assets/product-form.js, so the cart icon, the cart
 * drawer (including its auto-open setting) and cart apps react as they do for the product
 * page's own Add to cart button.
 */

const SELECTOR = '.product-card-sizes__add';
const ADDED_DISPLAY_DURATION = 2000;
const ERROR_DISPLAY_DURATION = 5000;

/** @type {WeakMap<Element, ReturnType<typeof setTimeout>>} */
const resetTimers = new WeakMap();

/**
 * @param {HTMLButtonElement} button
 * @param {'loading' | 'added' | 'error' | null} state
 * @param {string} [message]
 */
function setState(button, state, message = '') {
  const group = button.closest('[data-card-size-add]');
  const status = group?.querySelector('.product-card-sizes__status');

  clearTimeout(resetTimers.get(button));

  if (state) {
    button.dataset.state = state;
  } else {
    delete button.dataset.state;
  }

  button.disabled = state === 'loading';

  if (status) {
    status.textContent = message;
    status.classList.toggle('product-card-sizes__status--error', state === 'error');
  }

  if (state === 'added' || state === 'error') {
    const duration = state === 'added' ? ADDED_DISPLAY_DURATION : ERROR_DISPLAY_DURATION;
    resetTimers.set(
      button,
      setTimeout(() => setState(button, null), duration)
    );
  }
}

/**
 * @param {HTMLButtonElement} button
 */
async function addSize(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.dataset.state === 'loading') return;

  const group = button.closest('[data-card-size-add]');
  const productId = group instanceof HTMLElement ? group.dataset.productId : undefined;

  setState(button, 'loading');

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    }).then((result) => result.json());

    if (response.status) {
      // Unlike product-form.js, no CartAddEvent with didError here. That event exists for
      // multi-quantity requests where Shopify adds what stock allows; a card always asks for
      // one, so a failure adds nothing, and announcing it opens the cart drawer half-rendered.
      setState(button, 'error', response.description || response.message || '');
      return;
    }

    let cart;
    try {
      cart = await fetch('/cart.js').then((result) => result.json());
    } catch {
      cart = undefined;
    }

    setState(button, 'added', Theme.translations?.added || 'Added');

    button.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'ssstufff-card-size-add',
        itemCount: 1,
        productId,
        variantId,
        sections: response.sections,
      })
    );
  } catch (error) {
    console.error(error);
    setState(button, 'error', '');
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest(SELECTOR);
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  addSize(button);
});
